/**
 * @fileoverview CategoryReport - Main component for category filtering and reporting.
 *
 * @description This component provides:
 * - Category selector dropdown
 * - Date range picker (start and end dates, inclusive)
 * - Summary statistics broken down by transaction type
 * - Filtered transaction list with highlighted search category
 */
import { useState, useEffect } from 'react';
import { transactionAPI } from '../../services/api';
import { formatCurrency } from '../../utils/moneyUtils';
import ReportTransactionList from './ReportTransactionList';
import styles from './CategoryReport.module.css';

/**
 * Get the first and last day of the current month in YYYY-MM-DD format
 */
const getCurrentMonthDates = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    firstDay: formatDate(firstDay),
    lastDay: formatDate(lastDay)
  };
};

/**
 * Category Report Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.categoryHook - Category hook for category information
 * @param {Object} props.budgetInfo - Budget metadata including start month
 */
function CategoryReport({ categoryHook, budgetInfo }) {
  const { firstDay, lastDay } = getCurrentMonthDates();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Reset report state when budget changes
  useEffect(() => {
    setReportData(null);
    setHasSearched(false);
    setError(null);
    setSelectedCategory('');
    setStartDate(firstDay);
    setEndDate(lastDay);
  }, [budgetInfo?.budgetKey, firstDay, lastDay]);

  const handleSearch = async () => {
    if (!selectedCategory || !startDate || !endDate) {
      setError('Please select a category and both start and end dates');
      return;
    }

    // Validate start date is not after end date
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date cannot be after end date. Please adjust your date range.');
      return;
    }

    // Validate dates are not before budget start date
    if (budgetInfo?.startMonth) {
      const budgetStartDate = budgetInfo.startMonth.split('T')[0];
      if (new Date(startDate) < new Date(budgetStartDate)) {
        setError(`Start date cannot be before the budget start date (${budgetStartDate}).`);
        return;
      }
      if (new Date(endDate) < new Date(budgetStartDate)) {
        setError(`End date cannot be before the budget start date (${budgetStartDate}).`);
        return;
      }
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const data = await transactionAPI.getCategoryReport(selectedCategory, startDate, endDate);
      setReportData(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch report data');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setReportData(null);
    setHasSearched(false);
    setError(null);
  };

  // Get the selected category name
  const selectedCategoryName = categoryHook.categories?.find(
    cat => cat.categoryKey === parseInt(selectedCategory)
  )?.categoryName || '';

  return (
    <div className={styles.container}>
      {/* Search Filters Section */}
      <div className={styles.filtersSection}>
        <h2 className={styles.sectionTitle}>Search Filters</h2>

        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            <label className={styles.label}>Category:</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={styles.select}
            >
              <option value="">Select a category...</option>
              {categoryHook.categories?.map((cat) => (
                <option key={cat.categoryKey} value={cat.categoryKey}>
                  {cat.categoryName}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.label}>Start Date:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={budgetInfo?.startMonth ? budgetInfo.startMonth.split('T')[0] : undefined}
              className={styles.dateInput}
            />
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.label}>End Date:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || (budgetInfo?.startMonth ? budgetInfo.startMonth.split('T')[0] : undefined)}
              className={styles.dateInput}
            />
          </div>
        </div>

        <div className={styles.buttonRow}>
          <button onClick={handleSearch} className={styles.searchButton} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
          {hasSearched && (
            <button onClick={handleClearSearch} className={styles.clearButton}>
              Clear Results
            </button>
          )}
        </div>

        {error && (
          <div className={styles.errorMessage}>{error}</div>
        )}
      </div>

      {/* Results Section */}
      {hasSearched && reportData && !loading && (
        <>
          {/* Summary Statistics */}
          <div className={styles.summarySection}>
            <h2 className={styles.sectionTitle}>Summary for {selectedCategoryName}</h2>

            <div className={styles.overallSummary}>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Total Amount:</span>
                <span className={styles.summaryValue}>{formatCurrency(reportData.summary.total)}</span>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Total Credits (In):</span>
                <span className={`${styles.summaryValue} ${styles.creditValue}`}>
                  {formatCurrency(Math.abs(reportData.summary.credits))}
                </span>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Total Debits (Out):</span>
                <span className={`${styles.summaryValue} ${styles.debitValue}`}>
                  {formatCurrency(Math.abs(reportData.summary.debits))}
                </span>
              </div>
            </div>

            <h3 className={styles.breakdownTitle}>Breakdown by Transaction Type</h3>
            <div className={styles.typeBreakdownTable}>
              <div className={styles.tableHeader}>
                <span className={styles.headerCell}>Transaction Type</span>
                <span className={styles.headerCell}>Total</span>
                <span className={styles.headerCell}>Credits (In)</span>
                <span className={styles.headerCell}>Debits (Out)</span>
              </div>
              {reportData.summary.typeBreakdown.map((typeData) => (
                <div key={typeData.type} className={styles.tableRow}>
                  <span className={styles.cell}>{typeData.type}</span>
                  <span className={styles.cell}>{formatCurrency(typeData.total)}</span>
                  <span className={`${styles.cell} ${styles.creditCell}`}>
                    {formatCurrency(Math.abs(typeData.credits))}
                  </span>
                  <span className={`${styles.cell} ${styles.debitCell}`}>
                    {formatCurrency(Math.abs(typeData.debits))}
                  </span>
                </div>
              ))}
              {reportData.summary.typeBreakdown.length === 0 && (
                <div className={styles.emptyRow}>No transactions found for this category</div>
              )}
            </div>
          </div>

          {/* Transaction List */}
          <div className={styles.transactionsSection}>
            <h2 className={styles.sectionTitle}>Transactions</h2>
            <ReportTransactionList
              transactions={reportData.transactions}
              highlightCategoryKey={parseInt(selectedCategory)}
            />
          </div>
        </>
      )}

      {hasSearched && reportData && reportData.transactions.length === 0 && !loading && (
        <div className={styles.noResultsMessage}>
          No transactions found for {selectedCategoryName} between {startDate} and {endDate}
        </div>
      )}
    </div>
  );
}

export default CategoryReport;
